// import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import HardDriveIcon from "assets/icons/hard_drive.svg?react";
import ChatIcon from "assets/icons/mail_outline_new.svg?react";

import DayIcon from "assets/icons/dayIcon.svg?react";
import WeekIcon from "assets/icons/WeekIcon.svg?react";
import MonthIcon from "assets/icons/monthIcon.svg?react";
import ListIcon from "assets/icons/listIcon.svg?react";
import TaskIcon from "assets/icons/new-task.svg?react";
import NoteIcon from "assets/icons/note.svg?react";
import FolderIcon from "assets/icons/dossierNew.svg?react";
import DriveIcon from "assets/icons/hard_drive.svg?react";
import DocIcon from "assets/icons/doc.svg?react";
import PptIcon from "assets/icons/ppt.svg?react";
import XlsIcon from "assets/icons/xls.svg?react";
import OtherIcon from "assets/icons/other.svg?react";
import DocsIcon from "assets/icons/filters/docs.svg?react";
import CalendarIcon from "assets/icons/calendar.svg?react";
import CourtIcon from "assets/icons/court.svg?react";
import SendIcon from "assets/icons/sender.svg?react";
import React, { useState, useEffect, useRef, useContext } from "react";
import styles from "./styles.module.scss";

import SecondCheckbox from "components/SecondCheckbox";
import { useOnClickOutside } from "hooks/useClickOutside";
import { ECaseViews } from "components/CaseView/dto/ECaseViews";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AbilityContext, Can } from "contexts/AbilityContext";
import AddButton from "components/AddButton";
import ModalsManagerContext from "contexts/ModalsManager";
import {
  ScrollMenu,
  VisibilityContext,
  type publicApiType,
} from "react-horizontal-scrolling-menu";
import { styled, Tooltip } from "@mui/material";
import "react-horizontal-scrolling-menu/dist/styles.css";
import { ICase } from "dto/Cases/ICase";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretLeft,
  faCaretRight,
} from "@fortawesome/free-solid-svg-icons";

interface ICaseTab {
  id: ECaseViews;
  label: string;
  icon: React.FunctionComponent<React.SVGProps<SVGElement>>;
  fromCourtPortal: boolean;
}

const availableTabsBase: Omit<ICaseTab, "label">[] = [
  {
    id: "chat" as ECaseViews,
    icon: ChatIcon,
    fromCourtPortal: false,
  },
  {
    id: ECaseViews.COURTPORTAL,
    icon: CourtIcon,
    fromCourtPortal: true,
  },
  {
    id: "dossier" as ECaseViews,
    icon: HardDriveIcon,
    fromCourtPortal: false,
  },
  {
    id: "actions_day" as ECaseViews,
    icon: DayIcon,
    fromCourtPortal: false,
  },
  {
    id: "actions_week" as ECaseViews,
    icon: WeekIcon,
    fromCourtPortal: false,
  },
  {
    id: "actions_month" as ECaseViews,
    icon: MonthIcon,
    fromCourtPortal: false,
  },
  {
    id: "list" as ECaseViews,
    icon: ListIcon,
    fromCourtPortal: false,
  },
  {
    id: "tasks" as ECaseViews,
    icon: TaskIcon,
    fromCourtPortal: false,
  },
  {
    id: "notes" as ECaseViews,
    icon: NoteIcon,
    fromCourtPortal: false,
  },
];

const Arrow = ({
  children,
  onClick,
  testId,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: VoidFunction;
  className?: string;
  testId: string;
}) => {
  return (
    <div onClick={onClick} className={styles.arrowButton} data-testid={testId}>
      {children}
    </div>
  );
};

const LeftArrow = () => {
  const { t } = useTranslation();
  const visibility = useContext<publicApiType>(VisibilityContext);

  const disabled = visibility.useLeftArrowVisible();

  return (
    <>
      {!disabled && (
        <Arrow
          disabled={disabled}
          onClick={() => visibility.scrollPrev()}
          testId="left-arrow"
        >
          <Tooltip title={t("menu.scrollLeft")}>
            <div>
              <FontAwesomeIcon icon={faCaretLeft} />
            </div>
          </Tooltip>
        </Arrow>
      )}
    </>
  );
};

const RightArrow = () => {
  const { t } = useTranslation();
  const visibility = useContext<publicApiType>(VisibilityContext);
  const disabled = visibility.useRightArrowVisible();

  return (
    <>
      {!disabled && (
        <Arrow
          disabled={disabled}
          onClick={() => visibility.scrollNext()}
          testId="right-arrow"
        >
          <Tooltip title={t("menu.scrollRight")}>
            <div>
              <FontAwesomeIcon icon={faCaretRight} />
            </div>
          </Tooltip>
        </Arrow>
      )}
    </>
  );
};

const TabContainer = ({
  tab,
  onClick,
  view,
  activeView,
}: {
  tab: ICaseTab;
  itemId: string;
  onClick: (id: ECaseViews) => void;
  view: string;
  activeView: ECaseViews;
}) => {
  return (
    <div className={styles.tabItem}>
      <div
        onClick={() => onClick(tab.id)}
        className={`${styles.box} ${
          (view === tab.id || activeView === tab.id) && styles.activeText
        }`}
      >
        <tab.icon />
        {tab.label}
        {/* {calendarView === tab.id && <div className={styles.active}></div>} */}
      </div>

      <div className={styles.splitter}></div>
    </div>
  );
};

const NoScrollbar = styled("div")({
  "& .react-horizontal-scrolling-menu--scroll-container::-webkit-scrollbar": {
    display: "none",
  },
  "& .react-horizontal-scrolling-menu--scroll-container": {
    scrollbarWidth: "none",
    "-ms-overflow-style": "none",
  },
});

const CaseMenu = ({
  onClick,
  activeView,
  handleCreateFolder,
  handleCreateEditorDocument,
  Case,
}: {
  onClick: (tab: ECaseViews) => void;
  activeView?: ECaseViews;
  handleCreateFolder?: () => void;
  handleCreateEditorDocument?: (type: string) => void;
  Case?: ICase | null | undefined;
}) => {
  const { t } = useTranslation();
  const ability = useContext(AbilityContext);

  const availableTabs: ICaseTab[] = [
    { ...availableTabsBase[0], label: t("menu.correspondence") },
    { ...availableTabsBase[1], label: t("menu.courtFiles") },
    { ...availableTabsBase[2], label: t("menu.dossier") },
    { ...availableTabsBase[3], label: t("menu.day") },
    { ...availableTabsBase[4], label: t("menu.week") },
    { ...availableTabsBase[5], label: t("menu.month") },
    { ...availableTabsBase[6], label: t("menu.events") },
    { ...availableTabsBase[7], label: t("menu.tasks") },
    { ...availableTabsBase[8], label: t("menu.notes") },
  ];
  const [showCreateMenu, setShowCreateMenu] = useState<boolean>(false);
  const createMenuRef = useRef(null);
  useOnClickOutside(createMenuRef, () => setShowCreateMenu(false));
  const { modals, setModals, setOptions } = useContext(ModalsManagerContext);

  const tabSettingsFile = () => `caseTabs_${Case?.Id}`;

  const isTabVisible = (id: ECaseViews) => {
    switch (id) {
      case "dossier":
        return ability.can("accessModule", "drive");
      case "actions_day":
      case "actions_week":
      case "actions_month":
      case "tasks":
      case "list":
        return ability.can("accessModule", "tasks");
      case "notes":
        return ability.can("accessModule", "notes");
      case ECaseViews.COURTPORTAL:
        return Case?.FromPI;
    }

    return true;
  };

  const [visibleTabs, setVisibleTabs] = useState<ICaseTab[]>([]);

  const isFullScreen = window.location.href.search("fullscreen") !== -1;
  const navigate = useNavigate();

  const location = useLocation();
  const { caseId, view } = useParams();
  const queryParams = new URLSearchParams(location.search);
  const PDFId = queryParams.get("pdf");

  const handleNavigate = (type: ECaseViews) => {
    console.log("Handle navigate");
    if (onClick) {
      onClick(type);
      return;
    }

    const link = `/cases/${caseId}/${type}`;

    const query = new URLSearchParams(window.location.search);

    if (PDFId && !query.get("pdf")) {
      query.append("pdf", PDFId);
    }

    if (isFullScreen && !query.get("fullscreen")) {
      query.append("fullscreen", "1");
    }

    navigate(`${link}?${query}`);
  };

  const [activeTabs, setActiveTabs] = useState<string[]>([]);

  useEffect(() => {
    const storedTabs = localStorage.getItem(tabSettingsFile());
    if (storedTabs) {
      console.log("Stored tabs");
      setActiveTabs(JSON.parse(storedTabs));
    } else {
      console.log("Default tabs", isFullScreen);
      const defaultTabs = isFullScreen
        ? availableTabs
            .filter(
              (x) =>
                x.id !== ECaseViews.ACTION_DAY &&
                x.id !== ECaseViews.ACTION_WEEK &&
                x.id !== ECaseViews.LIST
            )
            .map((tab) => tab.id)
        : availableTabs
            .filter(
              (x) =>
                x.id !== ECaseViews.ACTION_DAY &&
                x.id !== ECaseViews.ACTION_WEEK
            )
            .map((tab) => tab.id);

      setActiveTabs(defaultTabs);
    }
  }, []);

  const handleToggleTab = (id: string) => {
    if (activeTabs.includes(id)) {
      setActiveTabs(activeTabs.filter((tabId) => tabId !== id));
      localStorage.setItem(
        tabSettingsFile(),
        JSON.stringify(activeTabs.filter((tabId) => tabId !== id))
      );
    } else {
      setActiveTabs([...activeTabs, id]);
      localStorage.setItem(
        tabSettingsFile(),
        JSON.stringify([...activeTabs, id])
      );
    }
  };

  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef(null);

  useOnClickOutside(ref, () => {
    if (!showDropdown) return;
    setShowDropdown(false);
  });

  useEffect(() => {
    const storedTabs = localStorage.getItem(tabSettingsFile());
    if (storedTabs) {
      setActiveTabs(JSON.parse(storedTabs));
    } else {
      const defaultTabs = isFullScreen
        ? availableTabs
            .filter(
              (x) =>
                x.id !== ECaseViews.ACTION_DAY &&
                x.id !== ECaseViews.ACTION_WEEK &&
                x.id !== ECaseViews.LIST
            )
            .map((tab) => tab.id)
        : availableTabs
            .filter(
              (x) =>
                x.id !== ECaseViews.ACTION_DAY &&
                x.id !== ECaseViews.ACTION_WEEK
            )
            .map((tab) => tab.id);
      setActiveTabs(defaultTabs);
    }
  }, [ability.rules, caseId]);

  useEffect(() => {
    setVisibleTabs(
      availableTabs.filter(
        (tab) => activeTabs.includes(tab.id) && isTabVisible(tab.id)
      )
    );
  }, [activeTabs]);

  return (
    <div className={styles.menu}>
      <div className={styles.tabs}>
        <NoScrollbar>
          <ScrollMenu LeftArrow={LeftArrow} RightArrow={RightArrow}>
            {visibleTabs.map((tab, idx) => (
              <TabContainer
                key={`tab_${idx}`}
                tab={tab}
                itemId={`tab_${idx}`}
                onClick={(id) => handleNavigate(id)}
                view={view}
                activeView={activeView}
              ></TabContainer>
            ))}
          </ScrollMenu>
        </NoScrollbar>
      </div>
      <div
        onClick={() => {
          if (showDropdown) return;
          setShowDropdown(!showDropdown);
        }}
        ref={ref}
        className={styles.box}
      >
        <div className={styles.add}>+ {t("menu.tabs")}</div>
        {showDropdown && (
          <div className={styles.dropdown}>
            <div className={styles.dropdownWrapper}>
              {availableTabs
                .filter((tab) => isTabVisible(tab.id))
                .map((tab) => (
                  <div key={tab.id} className={styles.dropdownBox}>
                    <SecondCheckbox
                      onChange={() => {
                        handleToggleTab(tab.id);
                      }}
                      value={activeTabs.includes(tab.id)}
                    />
                    {tab.label}
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      <div className={styles.right}>
        <AddButton
          className={styles.addButton}
          text={t("common.new")}
          onClick={() => setShowCreateMenu?.(!showCreateMenu)}
        />

        {showCreateMenu && (
          <div className={styles.createMenu} ref={createMenuRef}>
            {caseId && (
              <div
                onClick={() => {
                  setOptions({ case: Case });
                  setModals({
                    ...modals,
                    createPost: true,
                    hide: false,
                  });
                  setShowCreateMenu(false);
                }}
                className={styles.menuItem}
              >
                <div className={styles.icon}>
                  <SendIcon />
                </div>{" "}
                {t("menu.parcel")}
              </div>
            )}
            <Can I="accessModule" an="tasks">
              {caseId && (
                <div
                  onClick={() => {
                    setModals({
                      ...modals,
                      createEvent: true,
                      hide: false,
                    });
                    setShowCreateMenu(false);
                  }}
                  className={styles.menuItem}
                >
                  <div className={styles.icon}>
                    <CalendarIcon />
                  </div>
                  {t("menu.event")}
                </div>
              )}
              {caseId && (
                <div
                  onClick={() => {
                    setModals({
                      ...modals,
                      createTask: true,
                      hide: false,
                    });
                    setShowCreateMenu(false);
                  }}
                  className={styles.menuItem}
                >
                  <div className={styles.icon}>
                    <TaskIcon />
                  </div>
                  {t("menu.task")}
                </div>
              )}
            </Can>
            <Can I="accessModule" an="drive">
              <div className={styles.line}></div>
              {caseId && (
                <div
                  onClick={() => {
                    handleCreateFolder?.();
                    setShowCreateMenu(false);
                  }}
                  className={styles.menuItem}
                >
                  <div className={styles.icon}>
                    <FolderIcon />
                  </div>
                  {t("menu.folder")}
                </div>
              )}

              <div
                onClick={() => {
                  setModals({
                    ...modals,
                    createDossier: true,
                    hide: false,
                  });
                  setShowCreateMenu(false);
                }}
                className={styles.menuItem}
              >
                <div className={styles.icon}>
                  <DriveIcon />
                </div>
                {t("menu.fileOrLink")}
              </div>
              <div className={styles.line}></div>
              <div
                onClick={() => handleCreateEditorDocument("docx")}
                className={styles.menuItem}
              >
                <div className={styles.icon}>
                  <DocIcon />
                </div>
                Word
              </div>
              <div
                onClick={() => handleCreateEditorDocument("pptx")}
                className={styles.menuItem}
              >
                <div className={styles.icon}>
                  <PptIcon />
                </div>
                PowerPoint
              </div>
              <div
                onClick={() => handleCreateEditorDocument("xlsx")}
                className={styles.menuItem}
              >
                <div className={styles.icon}>
                  <XlsIcon />
                </div>
                Excel
              </div>
              <div
                onClick={() => handleCreateEditorDocument("googledoc")}
                className={styles.menuItem}
              >
                <div className={styles.icon}>
                  <DocsIcon />
                </div>
                Google Docs
              </div>
              <div
                onClick={() => handleCreateEditorDocument("other")}
                className={styles.menuItem}
              >
                <div className={styles.icon}>
                  <OtherIcon />
                </div>
                {t("menu.other")}
              </div>
            </Can>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaseMenu;
